import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import CheckPickList from '../../components/CheckPickList';
import { useCategoryTree, useCategoryZh } from '../../components/MaterialFieldInputs';
import { exportMaterials, listAllMaterials, MaterialListItem } from '../../api/material';
import { ApiError } from '../../api/request';
import { i18nText } from '../../utils/i18n';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';

// 导出前确认弹窗: 列出全部材料供勾选 (类别筛选 + 搜索过滤 + 全选), 确认后按勾选集导出.
// 勾满全部时不传过滤条件, 走后端整表导出 (GET), 避免超大 body.

interface Props {
    open: boolean;
    onClose: () => void;
}

function MaterialExportModal({ open, onClose }: Props) {
    const [items, setItems] = useState<MaterialListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [type1, setType1] = useState('');
    const [type2, setType2] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const categoryTree = useCategoryTree();
    const { cat1Zh, cat2Zh } = useCategoryZh();

    useEffect(() => {
        if (!open) return;
        setType1('');
        setType2('');
        setSelected(new Set());
        setError('');
        setLoading(true);
        (async () => {
            try {
                setItems(await listAllMaterials());
            } catch (e) {
                const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
                setError('加载材料列表失败: ' + msg);
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    // 类别筛选在已拉全量的本地列表上做 (type1/type2 存类别编码 categoryCode, 兼容历史中文值)
    const scoped = useMemo(() => {
        if (!type1 && !type2) return items;
        const t1Zh = cat1Zh(type1);
        const t2Zh = type2 ? cat2Zh(type1, type2) : '';
        return items.filter((m) => {
            if (type1 && m.type1 !== type1 && m.type1 !== t1Zh) return false;
            if (type2 && m.type2 !== type2 && m.type2 !== t2Zh) return false;
            return true;
        });
    }, [items, type1, type2, cat1Zh, cat2Zh]);

    // 类别切换后, 已勾选但不在当前类别范围内的项一并丢弃, 避免"导出了看不见的行"
    useEffect(() => {
        setSelected((prev) => {
            if (prev.size === 0) return prev;
            const inScope = new Set(scoped.map((m) => m.xcode));
            const next = new Set(Array.from(prev).filter((x) => inScope.has(x)));
            return next.size === prev.size ? prev : next;
        });
    }, [scoped]);

    const onExport = async () => {
        setExporting(true);
        setError('');
        try {
            await exportMaterials(
                selected.size === items.length ? undefined : Array.from(selected),
            );
            onClose();
        } catch (e) {
            const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError('导出失败: ' + msg);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Modal
            open={open}
            title="导出材料 · 材质编码 / SKU / 适配工艺类型"
            onClose={onClose}
            width={560}
        >
            <div className="space-y-3">
                {error && (
                    <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
                        {error}
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <div className="flex-1" title="按一级分类筛选">
                        <SearchSelect
                            className="w-full"
                            value={type1}
                            onChange={(v) => {
                                setType1(v);
                                setType2('');
                            }}
                            placeholder="全部类别"
                            options={zhOptions(categoryTree, (t) => t.categoryCode, (t) => t.nameZh)}
                            clearable
                        />
                    </div>
                    <div className="flex-1" title={type1 ? '按二级分类筛选' : '请先选择一级分类'}>
                        <SearchSelect
                            className="w-full"
                            value={type2}
                            disabled={!type1}
                            onChange={(v) => setType2(v)}
                            placeholder="全部二级"
                            options={zhOptions(
                                categoryTree.find((t) => t.categoryCode === type1)?.children || [],
                                (c) => c.categoryCode,
                                (c) => c.nameZh,
                            )}
                            clearable
                        />
                    </div>
                    {(type1 || type2) && (
                        <button
                            type="button"
                            className="btn-ghost text-slate-400 whitespace-nowrap"
                            title="清空类别筛选"
                            onClick={() => {
                                setType1('');
                                setType2('');
                            }}
                        >
                            清空
                        </button>
                    )}
                </div>
                <CheckPickList
                    items={scoped}
                    loading={loading}
                    keyOf={(m) => m.xcode}
                    searchText={(m) => `${m.xcode} ${i18nText(m.name)} ${m.name?.en || ''}`}
                    searchPlaceholder="搜索 材质编码 / 名称"
                    render={(m) => (
                        <>
                            <span className="font-mono text-xs">{m.xcode}</span>
                            <span className="text-xs text-slate-500 truncate">
                                {i18nText(m.name)}
                            </span>
                        </>
                    )}
                    selected={selected}
                    onChange={setSelected}
                />
                <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-slate-500">
                        已选 {selected.size} / {scoped.length} 项
                        {scoped.length !== items.length ? ` (全部 ${items.length})` : ''}
                    </span>
                    <div className="flex gap-2">
                        <button type="button" className="btn-ghost" onClick={onClose}>
                            取消
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={exporting || loading || selected.size === 0}
                            title={selected.size === 0 ? '请先勾选要导出的材料' : ''}
                            onClick={onExport}
                        >
                            {exporting ? '导出中…' : `导出已选 ${selected.size} 项`}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default MaterialExportModal;
